/**
 * Risk input conversion and profile resolution helpers for the Risk tab.
 *
 * Used by RiskTabContent, MobileRiskSection, and CascadeEditor
 * for risk value conversion and profile resolution.
 *
 * Display formatting (formatRisk) has been extracted to
 * utils/riskDisplayHelpers.ts as a shared utility.
 */

import type { AccountRisk, RiskSpec } from '@/types/schema/AccountRisk';

/** Convert stored value to display value for the input field */
export function toDisplayValue(risk: RiskSpec): string {
  if (risk.type === 'percentage') {
    return (risk.value * 100).toString();
  }
  return (risk.value / 100).toString();
}

/** Convert input display value to stored value */
export function toStoredValue(inputValue: number, type: 'percentage' | 'fixed'): number {
  if (type === 'percentage') {
    return inputValue / 100;
  }
  return Math.round(inputValue * 100);
}

/** Sensible default value when switching risk types */
export function defaultValueForType(type: 'percentage' | 'fixed'): number {
  // 1% for percentage, $100 for fixed
  return type === 'percentage' ? 0.01 : 10000;
}

/**
 * Resolve the initial selected profile ID using fallback chain:
 * defaultProfileId -> first active profile -> null
 */
export function resolveProfileId(risk: AccountRisk | null): string | null {
  if (!risk) return null;
  const activeProfiles = risk.riskProfiles.filter(p => p.status === 'active');
  const defaultId = risk.defaultProfileId;
  if (defaultId && activeProfiles.some(p => p.id === defaultId)) {
    return defaultId;
  }
  return activeProfiles[0]?.id ?? null;
}
